import { useHistory } from "react-router-dom";
import Form from "common/Form/Form";
import { LOGIN_PATH, MOBILE_SEARCH } from "common/constants";
import React, { useContext, useEffect } from "react";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { PrimaryTitle } from "features/auth/styles";
import useMedia from "utils/hooks/useMedia";
import { PasswordSection } from "features/auth/PasswordSection";
import AuthInlineError, {
  useAuthResultMessage,
} from "features/auth/AuthInlineError";
import Button from "common/Buttons/Button";
import Link from "common/Typography/Link";
import styled, { css, useTheme } from "styled-components";
import { sharedLib } from "@shared/services/shared.service";
import { triggerClearBiometric } from "services/webview/webViewBridgeUtils.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface Props {
  token: string;
}

const ResetPasswordForm = ({ token }: Props) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [resetPassword, { isLoading }] =
    sharedLib.sharedApi.authentication.useResetPasswordMutation();
  const history = useHistory();
  const { analytics } = useContext(AnalyticsContext);
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const { display: displayResultMessage, error } = useAuthResultMessage();
  const { isNewLook } = useTheme();

  useEffect(() => {
    analytics?.resetPasswordView();
  }, [analytics]);

  const onFinish = ({ password }: { password: string }) => {
    const args = {
      password,
      token,
    };
    displayResultMessage(
      resetPassword(args).unwrap(),
      t("reset_password.success"),
      t("reset_password.failed")
    )
      .then(() => {
        triggerClearBiometric().catch(() => {});
        history.push("/login");
      })
      .catch(() => {});
  };

  return (
    <Form
      name="login"
      initialValues={{
        remember: true,
      }}
      onFinish={onFinish}
      layout="vertical"
      requiredMark={false}
      style={{ width: "100%" }}
    >
      <PrimaryTitle $isMobile={isMobile} style={{ marginBottom: 24 }}>
        {t(
          isNewLook ? "reset_password.title_new_look" : "reset_password.title"
        )}
      </PrimaryTitle>
      <PasswordSection
        size="large"
        showPlaceholder={false}
        showConfirm={true}
        passwordLabel={t(
          isNewLook
            ? "reset_password.new_password_new_look"
            : "reset_password.new_password"
        )}
        confirmLabel={
          isNewLook ? t("reset_password.confirm_password_new_look") : undefined
        }
        autoComplete="new-password"
      />
      <ButtonsWrapper>
        <AuthInlineError message={error} style={{ margin: "4px 0 0" }} />
        <Button
          type="primary"
          $size="l"
          htmlType="submit"
          loading={isLoading}
          style={{ width: "100%", margin: "12px 0" }}
        >
          {t(
            isNewLook
              ? "reset_password.update_password_new_look"
              : "reset_password.update_password"
          )}
        </Button>
        <StyledLink onClick={() => history.push(LOGIN_PATH)}>
          {t(isNewLook ? "footer.login_new_look" : "footer.login")}
        </StyledLink>
      </ButtonsWrapper>
    </Form>
  );
};

export default ResetPasswordForm;

const ButtonsWrapper = styled.div`
  text-align: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
      > .ant-btn {
        margin: 8px 0 0 !important;
      }
    `}
`;
const StyledLink = styled(Link)`
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      font-size: 14px;
    `}
`;
